import React, { useMemo, FC } from 'react';

import { Container, Grid, styled, Typography } from '@material-ui/core';
import { Breakpoint } from '@material-ui/core/styles/createBreakpoints';
import withWidth, { isWidthUp } from '@material-ui/core/withWidth';
import { useTranslation } from 'react-i18next';
import Swiper from 'react-id-swiper';

import { BIG_MARGIN, colorsPalette, LARGE_MARGIN, MIDDLE_MARGIN } from '../../../../theme';
import { Pathway } from '../../../../types';
import { secondsToHHHmmm, splitIntoChunks } from '../../../../utils/helpers';
import PathwayCard from '../PathwayCard/PathwayCard';
import { StartCourseBtn } from '../StartCourseBtn';

const CHUNK_MOBILE_LENGTH = 3;

const swiperSettings = {
  slidesPerView: 1,
  spaceBetween: 20,
  pagination: {
    el: '.swiper-pagination',
    clickable: true,
  },
  mousewheel: {
    releaseOnEdges: true,
    forceToAxis: true,
    invert: true,
  },
  navigation: true,
  direction: 'horizontal',
  rebuildOnUpdate: true,
  slidesOffsetAfter: 0,
};

interface CoursePathwaysProps {
  width: Breakpoint;
  pathways: (Pathway | null)[];
  isCourseStarted: boolean;
  handleStartCourse: () => void;
}

const CoursesPathways: FC<CoursePathwaysProps> = ({
  width,
  pathways,
  handleStartCourse,
  isCourseStarted,
}) => {
  const { t } = useTranslation();

  const isMobile = !isWidthUp('sm', width);
  const isTablet = !isWidthUp('md', width);

  const filteredPathways = useMemo(() => {
    return pathways.filter((item) => Boolean(item)) as Pathway[];
  }, [pathways]);

  const videosCount = useMemo(() => {
    return filteredPathways.reduce((sum, item) => sum + (item?.count_videos || 0), 0);
  }, [filteredPathways]);

  const duration = useMemo(() => {
    return secondsToHHHmmm(filteredPathways.reduce((sum, item) => sum + (item?.duration || 0), 0));
  }, [filteredPathways]);

  const chunks = useMemo(() => {
    return isTablet ? splitIntoChunks(filteredPathways, CHUNK_MOBILE_LENGTH) : filteredPathways;
  }, [isTablet, filteredPathways]);

  return (
    <StyledContainer>
      <Heading variant="h2">{t('page.course.courseContent')}</Heading>
      <Description>
        {t('common.pathway', { count: filteredPathways.length })}
        {' • '}
        {t('common.videoCount', { count: videosCount })}
        {' • '}
        {duration}
      </Description>
      {isMobile ? (
        <div className="landscape-container">
          <Swiper {...swiperSettings}>
            {chunks.map((chunk, index) => (
              <div key={index}>
                {chunk.map(({ id, slug, name, count_videos, image_url, duration }) => (
                  <div key={id}>
                    <PathwayCard
                      link={`/pathway/${slug}`}
                      title={name}
                      videosCount={count_videos}
                      duration={duration}
                      image={image_url}
                    />
                  </div>
                ))}
              </div>
            ))}
          </Swiper>
        </div>
      ) : (
        <Grid container spacing={3}>
          {filteredPathways.map(({ id, slug, name, count_videos, image_url, duration }) => (
            <Grid key={id} xs={12} sm={6} md={3} item>
              <PathwayCard
                link={`/pathway/${slug}`}
                title={name}
                videosCount={count_videos}
                duration={duration}
                image={image_url}
              />
            </Grid>
          ))}
        </Grid>
      )}
      <Row>
        <StartCourseBtn isCourseStarted={isCourseStarted} handleStartCourse={handleStartCourse} />
      </Row>
    </StyledContainer>
  );
};

export default withWidth()(CoursesPathways);

const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: MIDDLE_MARGIN,
  paddingBottom: MIDDLE_MARGIN,
  [theme.breakpoints.up('md')]: {
    paddingTop: LARGE_MARGIN,
    paddingBottom: LARGE_MARGIN,
  },
}));
const Heading = styled(Typography)({
  marginBottom: 18,
});
const Description = styled(Typography)(({ theme }) => ({
  maxWidth: 800,
  marginBottom: 24,
  color: colorsPalette.gray,
  [theme.breakpoints.up('md')]: {
    marginBottom: BIG_MARGIN,
  },
}));
const Row = styled('div')(({ theme }) => ({
  marginTop: LARGE_MARGIN,
  textAlign: 'center',
  '& button': {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      maxWidth: 256,
    },
  },
}));
